/** Port von `BaseDialog/IwmDialogDokumentList.razor`. */

import { useCallback, useEffect, useState } from 'react';

import { documentApi } from '@/api/services/documentApi';
import {
  Dialog,
  IconButton,
  SimpleTable,
  useDialogService,
  type SimpleTableColumn,
} from '@/components/ui';
import type { Document } from '@/types/entities';

import { withMatchcode } from './matchcode';

export interface DocumentListDialogProps {
  open: boolean;
  onClose: () => void;
  matchcode: string;
  fetchDocuments: () => Promise<Document[]>;
}

export function DocumentListDialog(props: DocumentListDialogProps) {
  const { fetchDocuments, open } = props;
  const dialogService = useDialogService();
  const [documents, setDocuments] = useState<Document[]>([]);

  // Port von `Fetch()`; `Show()` lädt die Liste vor dem Anzeigen.
  const reload = useCallback(async () => {
    setDocuments(await fetchDocuments());
  }, [fetchDocuments]);

  useEffect(() => {
    if (!open) return;
    void reload();
  }, [open, reload]);

  // Port von `CommandClickHandler`, Fall `"open"`.
  const openDocument = (row: Document) => {
    window.open(documentApi.buildDownloadUrl(row.Token ?? ''), '_blank');
  };

  // Port von `CommandClickHandler`, Fall `"delete"`.
  const deleteDocument = async (row: Document) => {
    const confirmed = await dialogService.confirm(
      `Are you sure you want to permanently delete document ${row.FileName ?? ''}?`,
      'Delete Document',
    );
    if (confirmed) {
      await documentApi.remove(row.Id);
      await reload();
    }
  };

  const columns: SimpleTableColumn<Document>[] = [
    { field: 'Id', headerText: 'Id', visible: false, isPrimaryKey: true },
    { field: 'Bezeichnung', headerText: 'Bezeichnung' },
    {
      field: 'commands',
      headerText: '',
      template: (row) => (
        <>
          <IconButton
            iconClass="fa-solid fa-arrow-up-right-from-square"
            title="open"
            onClick={() => {
              openDocument(row);
            }}
          />
          <IconButton
            iconClass="fa-solid fa-trash"
            title="delete"
            onClick={() => deleteDocument(row)}
          />
        </>
      ),
    },
  ];

  return (
    <Dialog
      open={open}
      onClose={props.onClose}
      width="650px"
      showCloseIcon
      isModal
      header={withMatchcode('Documents', props.matchcode)}
    >
      <div style={{ marginBottom: '1rem' }}>
        <SimpleTable dataSource={documents} columns={columns} toolbar={['Search']} />
      </div>
    </Dialog>
  );
}

export default DocumentListDialog;
